// The link between the map and the sea view (seaview.md §4, R2492, R2493,
// R2495). Every environment opens both halves (R3111); this module only
// reads and writes addresses.
//
// The map to the sea view: SEA VIEW HERE of the VIEW panel opens
//
//   <sea view origin>/seaview/?world=<N>[&mode=<M>][&map=<this page>]#x=<px>&y=<px>[&f=<fleetId>]
//
// OPEN SEA VIEW of a hub seat card opens the same address with the mode
// of the seat and no fragment (R2828): with no x and y the sea view opens
// at the own home cell. `mode` names the mode the ticket of the sea view
// asks for (seaview.md §3.3 step 5, §4).
//
// The fragment leaves out `v`, `h`, `p` and `e`: a key the link leaves out
// takes the default of the sea view (seaview.md §4.1), and a fragment with
// no `v` reads as the current version. The map so carries no figure of the
// sea view.
//
// The sea view to the map: BACK TO MAP opens `/?world=<N>#at=<x>,<y>`, and
// the map reads `#at` at its first arrival (`readAtKnob`). The knob also
// marks the return, and the map then plays no arrival (R2975). SEA VIEW
// HERE writes `#at` into the map's own entry of the history, so the browser
// BACK button is a return too (`withAtKnob`).
//
// The socket address (R2494): on dev the sea view page stands at
// `/seaview/` of the game origin, and a game socket stands on the host of
// its world, so the sea view cannot find it alone. SEA VIEW HERE therefore
// stores the address the map dials in sessionStorage, under
// `<SEA_SOCKET_KEY_PREFIX>.<world>` (`rememberSeaSocket`), and the sea view
// socket dials it (seaview/src/net/socket.ts `socketBase`). The store
// lives in the tab, so it survives the navigation of the same tab, and a
// page on another origin, as in a local run, does not see it.
//
// A local run serves the map and the sea view on two ports. There the map
// page carries `?seaview=<origin of the sea view>`, the link goes to that
// origin and names this page in `map=`, and BACK TO MAP comes back to it
// (R2495). A value that is not an http or an https URL is ignored.

/** The page path of the sea view on its origin (seaview.md §2.1). */
export const SEAVIEW_PATH = '/seaview/';

/** The query key that names the sea view origin of a local run. */
export const SEAVIEW_PARAM = 'seaview';

/**
 * The prefix of the sessionStorage key of the world socket address. It is
 * `[net] socket_key_prefix` of seaview/data/seaview.toml; a change of one
 * is a change of the other.
 */
export const SEA_SOCKET_KEY_PREFIX = 'ap.seaview.socket';

/**
 * Stores the address `url` that the map dials for `world` (R2494). It
 * stores no ticket: the sea view resumes the seat by its token. A store
 * that throws, or an address that is not a `ws:` or a `wss:` URL, stores
 * nothing, and the sea view then dials `/ws` of its own origin.
 */
export function rememberSeaSocket(
  store: { setItem(k: string, v: string): void } | null, world: number, url: string,
): boolean {
  if (!store) return false;
  try {
    const u = new URL(url);
    if (u.protocol !== 'ws:' && u.protocol !== 'wss:') return false;
    store.setItem(`${SEA_SOCKET_KEY_PREFIX}.${world}`, u.toString());
    return true;
  } catch {
    return false;
  }
}

/** The place the sea view opens at. `follow` 0 follows no fleet. */
export interface SeaPlace {
  x: number;
  y: number;
  follow: number;
}

/**
 * The `#at=<x>,<y>` knob of the map, in world px (R2492), or null when the
 * fragment names none or a value is not a number. It is read from the
 * fragment as the other knobs are, so `at` beside `field=tex` or after `&`
 * reads too. A key that only ends in `at`, such as `heat=`, is not it.
 */
export function readAtKnob(
  hash: string = typeof location === 'undefined' ? '' : location.hash,
): { x: number; y: number } | null {
  const m = /(?:^#?|[#&;\s]|%20)at=([^&;\s#]+)/i.exec(hash);
  if (!m) return null;
  let raw: string;
  try {
    raw = decodeURIComponent(m[1]!);
  } catch {
    return null;
  }
  const parts = raw.split(',');
  if (parts.length !== 2 || parts.some((p) => p.trim() === '')) return null;
  const x = Number(parts[0]);
  const y = Number(parts[1]);
  return Number.isFinite(x) && Number.isFinite(y) ? { x, y } : null;
}

/**
 * The address `page` with the `#at` knob set to `at` in whole world px. The
 * other knobs of the fragment stay, and an older `at` goes (R2975).
 */
export function withAtKnob(page: string, at: { x: number; y: number }): string {
  const url = new URL(page);
  const kept = url.hash.slice(1).split('&').filter((k) => k !== '' && !/^at=/i.test(k));
  url.hash = [...kept, `at=${Math.round(at.x)},${Math.round(at.y)}`].join('&');
  return url.toString();
}

/** The origin a `?seaview=` value names, or null for none or a bad one. */
function namedOrigin(raw: string | null): string | null {
  if (!raw) return null;
  try {
    const u = new URL(raw);
    return u.protocol === 'http:' || u.protocol === 'https:' ? u.origin : null;
  } catch {
    return null;
  }
}

/**
 * The address of the sea view at `place` in `world`, from the page at
 * `page` (its `location.href`). Without `?seaview=` the sea view is on the
 * origin of the page; with it, the link goes to that origin and names this
 * page, less its fragment, in `map=`. A null `place` writes no fragment,
 * and the sea view opens at the own home cell. A `mode` other than ''
 * goes into `mode=` (R2828).
 */
export function seaViewHref(page: string, world: number, place: SeaPlace | null, mode = ''): string {
  const here = new URL(page);
  const other = namedOrigin(here.searchParams.get(SEAVIEW_PARAM));
  const url = new URL(SEAVIEW_PATH, other ?? here.origin);
  url.searchParams.set('world', String(world));
  if (mode !== '') url.searchParams.set('mode', mode);
  if (other !== null) {
    here.hash = '';
    url.searchParams.set('map', here.toString());
  }
  if (place !== null) {
    const parts = [`x=${Math.round(place.x)}`, `y=${Math.round(place.y)}`];
    if (place.follow > 0) parts.push(`f=${place.follow}`);
    url.hash = parts.join('&');
  }
  return url.toString();
}

/** The query key of the embed mode of the sea view (R2865). */
export const SEAVIEW_EMBED_PARAM = 'embed';

/**
 * The address of the frame of the idle sea view (R2865): `seaViewHref` at
 * `at` with no fleet to follow, plus `embed=1`. The sea view in embed mode
 * draws one frame, holds its loop and waits for the map's `play`. A local
 * run names this page in `map=`, so the sea view posts its messages to the
 * map's origin.
 */
export function seaIdleHref(page: string, world: number, at: { x: number; y: number }, mode = ''): string {
  const url = new URL(seaViewHref(page, world, { x: at.x, y: at.y, follow: 0 }, mode));
  url.searchParams.set(SEAVIEW_EMBED_PARAM, '1');
  return url.toString();
}
